import { useRef, useState } from "react";

// Presentation only. The underlying <input type="file"> is the exact same
// element the plain file picker always was — same id/name/accept/onChange —
// just visually hidden and opened programmatically. Dropping a file calls
// that same onChange with a synthetic {target: {files}}, so the caller's
// existing handler (extension/size checks, state updates) never needs to
// know whether the file arrived via click or drag-and-drop.
function formatFileSize(bytes) {
    if (typeof bytes !== "number" || Number.isNaN(bytes)) return "";
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB"];
    let value = bytes / 1024;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    return `${value.toFixed(1)} ${units[unitIndex]}`;
}

export default function FileDropzone({
    id,
    name,
    accept,
    acceptedExtensionsLabel,
    file,
    error,
    uploadProgress = 0,
    isUploading = false,
    onChange,
    onRemove,
}) {
    const inputRef = useRef(null);
    const [isDragActive, setIsDragActive] = useState(false);

    function openPicker() {
        inputRef.current?.click();
    }

    function handleDrop(e) {
        e.preventDefault();
        setIsDragActive(false);
        if (e.dataTransfer.files?.length) {
            onChange({ target: { files: e.dataTransfer.files } });
        }
    }

    function handleRemoveClick(e) {
        e.stopPropagation();
        // Cleared here (not by the caller) so picking the exact same file
        // again right after removing it still fires a change event —
        // browsers don't re-fire onChange for an input whose value already
        // names that file.
        if (inputRef.current) inputRef.current.value = "";
        onRemove();
    }

    function handleReplaceClick(e) {
        e.stopPropagation();
        openPicker();
    }

    return (
        <div
            className={`wizard-dropzone${isDragActive ? " drag-active" : ""}${file ? " has-file" : ""}`}
            onClick={!file ? openPicker : undefined}
            onDragOver={(e) => {
                e.preventDefault();
                setIsDragActive(true);
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                setIsDragActive(false);
            }}
            onDrop={handleDrop}
            role={!file ? "button" : undefined}
            tabIndex={!file ? 0 : undefined}
            onKeyDown={
                !file
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openPicker();
                          }
                      }
                    : undefined
            }
        >
            <input ref={inputRef} id={id} name={name} type="file" accept={accept} onChange={onChange} className="wizard-dropzone-input" />

            {!file ? (
                <div className="wizard-dropzone-empty">
                    <div className="wizard-dropzone-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
                            <path d="M7 18a4.5 4.5 0 0 1-1.5-8.74A5 5 0 0 1 15 8.5c.17-.01.33-.02.5-.02a4.5 4.5 0 0 1 0 9H7Z" />
                            <path d="M12 12v6M9.5 14.5 12 12l2.5 2.5" />
                        </svg>
                    </div>
                    <div className="wizard-dropzone-text">
                        <strong>Click to upload</strong> or drag and drop
                    </div>
                    {acceptedExtensionsLabel && <div className="wizard-dropzone-hint">Supports {acceptedExtensionsLabel}</div>}
                </div>
            ) : (
                <div className="wizard-dropzone-file">
                    <div className="wizard-dropzone-file-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z" />
                        </svg>
                    </div>
                    <div className="wizard-dropzone-file-info">
                        <div className="wizard-dropzone-file-name">{file.name}</div>
                        <div className="wizard-dropzone-file-size">{formatFileSize(file.size)}</div>
                        {isUploading && (
                            <div className="content-file-progress mt-2">
                                <div className="content-file-progress-bar" style={{ width: `${uploadProgress}%` }} />
                            </div>
                        )}
                    </div>
                    <div className="wizard-dropzone-file-actions">
                        <button type="button" className="wizard-dropzone-action" onClick={handleReplaceClick}>
                            Replace
                        </button>
                        <button type="button" className="wizard-dropzone-action danger" onClick={handleRemoveClick}>
                            Remove
                        </button>
                    </div>
                </div>
            )}

            {error && <div className="invalid-feedback d-block wizard-dropzone-error">{error}</div>}
        </div>
    );
}
